import React, { useState } from 'react';
import { formatTime12h, getCookie } from '../utils'; 
import UpiPayment from './UpiPayment'; // Changed from MockPaymentGateway

// UPDATED: Added enrollments array to props to calculate utilisation per batch
export default function ViewBatches({ batches, selectedProgram, setSelectedProgram, setActiveTab, setSelectedBatchId, userRole, userUid, fetchEnrollments, enrollments = [] }) {
  const [search, setSearch] = useState('');
  const [paymentConfig, setPaymentConfig] = useState({
    isOpen: false,
    itemName: '',
    amount: 0,
    batchId: null
  });

  const filtered = batches.filter(b =>
    (b.Type === selectedProgram || b.type === selectedProgram) &&
    (b.Title || b.title).toLowerCase().includes(search.toLowerCase()) &&
    (userRole === 'admin' || b.active !== false && b.isActive !== false)
  );

  // --- CAPACITY & UTILISATION LOGIC ---
  const getProgressColor = (percent) => {
    if (percent >= 90) return '#ef4444'; // Red (Nearing capacity)
    if (percent >= 75) return '#f59e0b'; // Amber/Orange
    return '#10b981'; // Green
  };

  // --- API INTEGRATION FUNCTION ---
  const handlePaymentSuccess = async () => {
    try {
      // 1. Find the full batch details from local state
      const batch = batches.find(b => (b.id || b._id) === paymentConfig.batchId);
      if (!batch) return alert("Batch details missing!");
      
      if (!userUid) {
         return alert("Student Profile ID missing. Please ensure your profile is fully set up first!");
      }
      
      const apiHeaders = {
        'Content-Type': 'application/json'
      };

      // 2. Calculate Dates
      const today = new Date();
      const expiry = new Date(today);
      // Add the valid days from the batch to today's date
      expiry.setDate(today.getDate() + (batch.ValidDays || batch.validDays || 30)); 

      // 3. Map exactly to your Mongoose EnrolmentSchema
      const payload = {
        studentId: userUid,
        batchId: batch._id || batch.id,
        EnrolledDate: today.toISOString(),
        ExpiryDate: expiry.toISOString(),
        TotalHours: batch.TotalHours || batch.hoursCount || 0,
        UsedHours: 0, // Starts at 0
        TotalFees: batch.Fees || batch.fees || 0,
        PaidFees: paymentConfig.amount || 0, 
        active: true
      };

      const token = getCookie('tsa_token');
      
      // 4. Send POST request to create the enrollment
      const response = await fetch('https://tsaapis.operate.live/api/enrolments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token || ''
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to save enrollment to database");
      
      const savedEnrolment = await response.json(); // Need the inserted ID

      // 3. PREPARE PAYMENT PAYLOAD
      const paymentPayload = {
        EnrolmentId: savedEnrolment._id,
        PaidAmount: payload.PaidFees,
        PaidDT: today.toISOString(),
        Mode: 'Payment Gateway',
        TransactionRef: '',
        Status: 'Pending',
        Remarks: "Enrollment done online by Student"
      };

      // 4. POST TO PAYMENTS API
      const payRes = await fetch('https://tsaapis.operate.live/api/payments', {
        method: 'POST',
        headers: apiHeaders,
        credentials: 'include',
        body: JSON.stringify(paymentPayload)
      });

      if (!payRes.ok) throw new Error("Failed to create payment record");

      // 5. Cleanup & Redirect
      alert(`Successfully Enrolled in ${batch.Title || batch.title}!`);
      
      if (fetchEnrollments) {
        await fetchEnrollments(); // Refresh global enrollments state if passed
      }
      
      // Auto-redirect the user to their dashboard to see their new active plan!
      setActiveTab('dashboard');

    } catch (error) {
      console.error("Enrollment API Error:", error);
      alert("Payment processed, but we encountered an error saving your enrollment. Please contact admin.");
    }
  };

  return (
    <div className="admin-card">
      <h2>
        <span>{selectedProgram} Batches</span>

        <div style={{ display: 'flex', gap: '10px' }}>
          {userRole === 'admin' && (
            <button className="btn-small btn-success" onClick={() => setActiveTab('create')}>
              <i className="fas fa-plus"></i> Create Batch
            </button>
          )}

          <button className="btn-small" onClick={() => setSelectedProgram(null)}>
            <i className="fas fa-arrow-left"></i> Go Back
          </button>
        </div>
      </h2>

      <div className="filter-toolbar">
        <input
          type="text"
          className="filter-input"
          placeholder={`Search ${selectedProgram} batches...`}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Batch Title</th>
              <th>Timings</th>
              <th>Hours</th>
              <th>Utilisation</th> {/* NEW COLUMN */}
              <th>Fees</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>No batches found for this program.</td></tr>
            ) : (
              filtered.map(b => {
                const bId = b._id || b.id;
                const bTitle = b.Title || b.title;
                const bFees = b.Fees || b.fees;
                const bCapacity = Number(b.Capacity || b.capacity) || 20; // Default 20
                const bActive = b.active !== false && b.isActive !== false;
                
                // Calculate Utilisation
                const currentUtilisation = enrollments.filter(e => e.batchId === bId).length;
                const utilisationPercent = bCapacity > 0 ? Math.min(100, Math.round((currentUtilisation / bCapacity) * 100)) : 0;

                return (
                  <tr key={bId} style={{ opacity: bActive ? 1 : 0.6 }}>
                    <td>
                      <strong>{bTitle}</strong>
                      {!bActive && userRole === 'admin' && (
                        <span className="badge" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: '1px solid #ef4444', marginLeft: '8px', fontSize: '0.7rem' }}>Inactive</span>
                      )}
                    </td>
                    <td>{formatTime12h(b.StartTime || b.startTime)} - {formatTime12h(b.EndTime || b.endTime)}</td>
                    <td>
                        {b.TotalHours || b.hoursCount} Hrs<br />
                        <small style={{ color: '#64748b' }}>Valid: {b.ValidDays || b.validDays} Days</small>
                    </td>
                    
                    {/* NEW UTILISATION PROGRESS BAR CELL */}
                    <td style={{ minWidth: '130px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-gray)', marginBottom: '5px', fontWeight: 'bold' }}>
                        <span>{currentUtilisation} Enrolled</span>
                        <span>{bCapacity} Max</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${utilisationPercent}%`,
                          backgroundColor: getProgressColor(utilisationPercent),
                          transition: 'width 0.5s ease-out, background-color 0.5s ease'
                        }}></div>
                      </div>
                    </td>

                    <td><strong style={{ color: 'var(--accent-gold)' }}>₹{bFees}</strong></td>
                    <td>

                      {userRole === 'admin' ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button className="btn-small btn-success" onClick={() => { setSelectedBatchId(bId); setActiveTab('batch-details'); }}>
                            <i className="fas fa-users"></i> Enrollments
                          </button>
                          <button className="btn-small btn-action" onClick={() => { setSelectedBatchId(bId); setActiveTab('edit-batch'); }}>
                            <i className="fas fa-edit"></i> Edit
                          </button>
                        </div>
                      ) : (
                        <button
                          className="submit-btn"
                          disabled={currentUtilisation >= bCapacity} // Prevent enrollment if full
                          style={{
                            opacity: currentUtilisation >= bCapacity ? 0.5 : 1,
                            cursor: currentUtilisation >= bCapacity ? 'not-allowed' : 'pointer',
                            backgroundColor: currentUtilisation >= bCapacity ? '#64748b' : ''
                          }}
                          onClick={() => setPaymentConfig({
                            isOpen: true,
                            itemName: `Enroll: ${bTitle}`,
                            amount: bFees || 0, 
                            batch: b
                          })}
                        >
                          <i className={currentUtilisation >= bCapacity ? "fas fa-ban" : "fas fa-sync-alt"}></i> 
                          {currentUtilisation >= bCapacity ? ' Full' : ' Enroll'}
                        </button>
                      )}

                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* UPI PAYMENT COMPONENT (Wrapped in modal overlay) */}
      {paymentConfig.isOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(5px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 999999
        }}>
          <div className="admin-card" style={{ 
            width: '100%', maxWidth: '400px', padding: '30px', 
            position: 'relative', borderTop: '4px solid var(--accent-gold)',
            textAlign: 'center'
          }}>
            <button 
              onClick={() => setPaymentConfig({ ...paymentConfig, isOpen: false })}
              style={{
                position: 'absolute', top: '15px', right: '15px', background: 'none', 
                border: 'none', color: 'var(--text-gray)', fontSize: '1.2rem', cursor: 'pointer'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
            
            <p style={{ color: 'var(--text-gray)', marginBottom: '15px' }}>{paymentConfig.itemName}</p>
            
            <UpiPayment 
              batch={paymentConfig.batch}
              studentId={userUid}
              setOpenUpiPayment={() => setPaymentConfig({ ...paymentConfig, isOpen: false })}
              setEnrollments={async () => await fetchEnrollments()}
              // IMPORTANT: You will likely need to update your UpiPayment component to accept 
              // an `onSuccess` prop so it knows when to trigger handlePaymentSuccess()
              onSuccess={() => {
                setPaymentConfig({ ...paymentConfig, isOpen: false });
                handlePaymentSuccess();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}